
import { defineComponent, PropType } from 'vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import ReportChartView from '@/components/ReportChartView.vue'
import ReportTableView from '@/components/ReportTableView.vue'
import AiReportService from '@/services/ai-report-service'
import {
  AiReportChatMessage,
  AiReportColumn,
  AiReportResponse,
  ReportChart,
  ReportChartOption,
  ReportChartType,
  ReportData,
  ReportTable
} from '@/model/models'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  caption?: string
  kind?: 'result' | 'clarification' | 'error'
  chart?: ReportChart
  table?: ReportTable
  data?: ReportData
  rowCount?: number
  executionTimeMs?: number
}

export default defineComponent({
  name: 'AiReportChat',
  components: { PulseLoader, ReportChartView, ReportTableView },
  props: {
    // Both optional now: the admin can ask without a UI date range or chart type selected - when
    // absent, the backend infers dates from the question's wording (or skips date filtering), and
    // no chart is rendered for that message (table only).
    dateFrom: { type: Number as PropType<number | null>, default: null },
    dateTo: { type: Number as PropType<number | null>, default: null },
    dateRangeLabel: { type: String, default: '' },
    chartType: { type: String as PropType<string | null>, default: null }
  },
  data() {
    return {
      messages: [] as ChatMessage[],
      history: [] as AiReportChatMessage[],
      draft: '',
      loading: false,
      // Sent-prompt recall, PuTTY/shell-style: Up/Down only take over when the input is otherwise
      // empty, so normal cursor movement while composing a message is untouched.
      sentPrompts: [] as string[],
      historyIndex: 0
    }
  },
  methods: {
    reset() {
      this.messages = []
      this.history = []
      this.draft = ''
      this.sentPrompts = []
      this.historyIndex = 0
    },
    onHistoryUp(event: KeyboardEvent) {
      if (this.draft.trim().length > 0 || this.historyIndex === 0) {
        return
      }
      event.preventDefault()
      this.historyIndex--
      this.draft = this.sentPrompts[this.historyIndex]
    },
    onHistoryDown(event: KeyboardEvent) {
      if (this.historyIndex >= this.sentPrompts.length) {
        return
      }
      event.preventDefault()
      this.historyIndex++
      this.draft = this.historyIndex < this.sentPrompts.length ? this.sentPrompts[this.historyIndex] : ''
    },
    send() {
      const prompt = this.draft.trim()
      if (!prompt || this.loading) {
        return
      }

      this.messages.push({
        role: 'user',
        content: prompt,
        caption: this.captionText
      })
      this.sentPrompts.push(prompt)
      this.historyIndex = this.sentPrompts.length
      this.draft = ''
      this.loading = true
      this.scrollToBottom()

      AiReportService.query({
        prompt,
        dateFrom: this.dateFrom ?? undefined,
        dateTo: this.dateTo ?? undefined,
        chartTypeHint: this.chartType ?? undefined,
        conversationHistory: this.history
      }).then((response: AiReportResponse) => {
        this.history.push({ role: 'user', content: prompt })
        this.handleResponse(response)
      }).catch(() => {
        this.messages.push({
          role: 'assistant',
          kind: 'error',
          content: 'Something went wrong reaching the AI report service. Please try again.'
        })
      }).finally(() => {
        this.loading = false
        this.scrollToBottom()
      })
    },
    handleResponse(response: AiReportResponse) {
      if (response.type === 'result') {
        const data = new ReportData({ name: 'ai-result', columns: response.columns.map(c => c.name), data: response.rows })
        const chart = this.buildChart(response.columns)
        const table = this.buildTable(response.columns)

        this.messages.push({
          role: 'assistant',
          kind: 'result',
          content: response.explanation || '',
          chart: chart || undefined,
          table,
          data,
          rowCount: response.rows.length,
          executionTimeMs: response.executionTimeMs
        })

        if (response.explanation) {
          this.history.push({ role: 'assistant', content: response.explanation })
        }
      } else if (response.type === 'clarification') {
        const message = response.message || ''
        this.messages.push({ role: 'assistant', kind: 'clarification', content: message })
        this.history.push({ role: 'assistant', content: message })
      } else {
        this.messages.push({ role: 'assistant', kind: 'error', content: response.message || 'Something went wrong.' })
      }
    },
    // Axis = first returned column, series = remaining numeric columns (auto-inferred - chart
    // choice is a frontend concern, the backend never decides this). No chart type selected, or
    // no numeric columns to chart (e.g. a single aggregate value) - skip the chart, the table
    // still renders either way.
    buildChart(columns: AiReportColumn[]): ReportChart | null {
      if (!this.chartType || columns.length < 2) {
        return null
      }

      const axisCol = columns[0]
      const numericCols = columns.slice(1).filter(c => c.type === 'number')
      if (numericCols.length === 0) {
        return null
      }

      const seriesCols = this.chartType === ReportChartType.Pie ? numericCols.slice(0, 1) : numericCols

      return new ReportChart({
        title: '',
        type: this.chartType,
        options: [ReportChartOption.Legend],
        dataset: 'ai-result',
        axis: { title: axisCol.label, value: axisCol.name, format: axisCol.type === 'date' ? 'YYYY-MM-DD' : '' },
        // No thousands-separator comma here, unlike buildTable() below: ReportChartView's
        // axisValues() returns these as formatted strings, which feed directly into Chart.js's
        // data array. Chart.js parses them numerically, and JS numeric parsing stops at the
        // first non-digit char - "12,036.00" silently parses as just 12. A comma-free format
        // avoids that; the table is unaffected since it only ever displays the string as text.
        series: seriesCols.map(c => ({ title: c.label, value: c.name, format: '0.00' }))
      })
    },
    buildTable(columns: AiReportColumn[]): ReportTable {
      return new ReportTable({
        title: '',
        dataset: 'ai-result',
        columns: columns.map(c => ({
          title: c.label,
          value: c.name,
          link: '',
          format: c.type === 'number' ? '0,0.00' : (c.type === 'date' ? 'YYYY-MM-DD HH:mm' : '')
        }))
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.thread as HTMLElement
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    }
  },
  computed: {
    chartTypeLabel(): string {
      return this.chartType ? this.chartType.charAt(0).toUpperCase() + this.chartType.slice(1) : ''
    },
    captionText(): string {
      const parts = []
      if (this.dateRangeLabel) {
        parts.push(this.dateRangeLabel)
      }
      if (this.chartTypeLabel) {
        parts.push(this.chartTypeLabel)
      }
      return parts.join(' · ')
    }
  }
})
